import { Permission, TenantConfigPlane } from '@today/types'
import { ImportDefinitionList } from '@today/ui-domain'
import { useState } from 'react'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useImportDefinitions } from '@/hooks/useImportDefinitions'
import { type DesignerState, designerOf, newDesigner } from './designer-state'
import { ImportDesigner } from './ImportDesigner'

interface Access {
	canRead: boolean
	canManage: boolean
}

/**
 * Who sees what (TO-400 REQ-007): on the own-tenant plane, `import_definition.read`
 * lists and `import_definition.manage` designs; on the platform plane the
 * Super-Admin does both. A READ-only manifest grant never designs.
 */
const useAccess = (plane: TenantConfigPlane, readOnly: boolean): Access => {
	const { permissions } = useViewerPermissions()
	if (plane === TenantConfigPlane.PLATFORM) return { canRead: true, canManage: !readOnly }
	return {
		canRead: permissions.includes(Permission.IMPORT_DEFINITION_READ),
		canManage: !readOnly && permissions.includes(Permission.IMPORT_DEFINITION_MANAGE),
	}
}

export interface ImportDefinitionsPanelProps {
	tenantId: string
	plane: TenantConfigPlane
	readOnly: boolean
}

/** The tenant's import definitions and the visual designer (TO-400), inside the File exchange section. */
export function ImportDefinitionsPanel({ tenantId, plane, readOnly }: ImportDefinitionsPanelProps) {
	const { canRead, canManage } = useAccess(plane, readOnly)
	const defs = useImportDefinitions(tenantId, plane)
	const [designing, setDesigning] = useState<{ id: number; state: DesignerState } | null>(null)
	if (!canRead) return null
	const open = (state: DesignerState) => setDesigning((current) => ({ id: (current?.id ?? 0) + 1, state }))
	if (designing && canManage) {
		return (
			<ImportDesigner
				key={designing.id}
				tenantId={tenantId}
				plane={plane}
				initial={designing.state}
				save={defs.save}
				activate={defs.activate}
				onClose={() => setDesigning(null)}
			/>
		)
	}
	return (
		<div className="flex flex-col gap-2">
			<h3 className="font-ds-heading text-ds-lg">Import definitions</h3>
			{defs.loadFailed && <p role="alert">Could not load the import definitions.</p>}
			<ImportDefinitionList
				definitions={defs.definitions ?? []}
				canManage={canManage}
				onNew={() => open(newDesigner())}
				onEdit={(key, version) =>
					void defs
						.loadVersion(key, version)
						.then((v) =>
							open(designerOf(key, v.contract, { version: v.version, status: v.status, revision: v.revision })),
						)
				}
				onActivate={(key, version) => void defs.activate(key, version)}
			/>
		</div>
	)
}
